import { useCallback, useEffect, useMemo, useState } from "react";
import { message } from "antd";
import dayjs from "dayjs";
import { deleteTransactionSchedule, getTransactionScheduleAllocationStatusLogs, getTransactionScheduleManageDetail, getTransactionSchedules } from "../api/transactionScheduleApi";
import { getUserRoles } from "../api/auth";
import useBatchProgress from "./useBatchProgress";
import useTransactionScheduleReturns from "./useTransactionScheduleReturns";
import useTransactionScheduleManageActions from "./useTransactionScheduleManageActions";
import { buildTransactionScheduleSummary } from "../components/transactionSchedule/transactionScheduleSummary";

export default function useTransactionSchedulePageData(location) {
    const [transactions, setTransactions] = useState([]);
    const [loadingTable, setLoadingTable] = useState(false);

    const [searchRequestNumber, setSearchRequestNumber] = useState("");
    const [searchPayer, setSearchPayer] = useState("");
    const [selectedParentDisplayStatus, setSelectedParentDisplayStatus] = useState(undefined);
    const [selectedCreatedAtRange, setSelectedCreatedAtRange] = useState([]);

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [detailData, setDetailData] = useState(null);
    const [statusLogs, setStatusLogs] = useState([]);
    const [loadingDetails, setLoadingDetails] = useState(false);

    const [manageOpen, setManageOpen] = useState(false);
    const [manageRecord, setManageRecord] = useState(null);

    const batch = useBatchProgress();

    const {
        returns,
        returnHistory,
        ignoredReturns,
        loadingUnmatchedReturns,
        loadingReturnHistory,
        loadingIgnoredReturns,
        loadUnmatchedReturns,
        loadReturnHistory,
        loadIgnoredReturns,
        refreshReturnsData,
    } = useTransactionScheduleReturns();

    const token = localStorage.getItem("access_token");
    const roles = getUserRoles(token);

    const hasRole = useCallback(
        (roleName) => roles.includes(roleName) || roles.includes(`ROLE_${roleName}`),
        [roles],
    );

    const canAgree = hasRole("ADMIN") || hasRole("MODERATOR");
    const canUnagree = hasRole("ADMIN") || hasRole("MODERATOR");
    const isAuthor = hasRole("AUTHOR");
    const canDelete = hasRole("ADMIN") || hasRole("MODERATOR") || isAuthor;

    const loadTransactions = useCallback(async () => {
        setLoadingTable(true);
        try {
            const data = await getTransactionSchedules();
            const rows = (data || []).map((item) => ({ ...item, key: item.id }));
            setTransactions(rows);
            return rows;
        } catch (error) {
            message.error(error.message || "Гүйлгээний хуваарь ачаалж чадсангүй");
            return [];
        } finally {
            setLoadingTable(false);
        }
    }, []);

    const loadAll = useCallback(async () => {
        await Promise.all([
            loadTransactions(),
            refreshReturnsData(),
        ]);
    }, [loadTransactions, refreshReturnsData]);

    useEffect(() => {
        loadAll();
    }, [loadAll]);

    const openManage = useCallback((record) => {
        setManageRecord(record);
        setManageOpen(true);
    }, []);

    const refreshDetails = useCallback(async (recordId) => {
        const [detail, logs] = await Promise.all([
            getTransactionScheduleManageDetail(recordId),
            getTransactionScheduleAllocationStatusLogs(recordId),
        ]);

        setDetailData(detail || null);
        setStatusLogs(
            (logs || []).map((item, index) => ({
                ...item,
                key: item.id || index,
            })),
        );
    }, []);

    const openDetails = useCallback(
        async (record) => {
            setSelectedRecord(record);
            setDrawerOpen(true);
            setLoadingDetails(true);

            try {
                await refreshDetails(record.id);
            } catch (error) {
                message.error(error.message || "Дэлгэрэнгүй мэдээлэл ачаалж чадсангүй");
                setDetailData(null);
                setStatusLogs([]);
            } finally {
                setLoadingDetails(false);
            }
        },
        [refreshDetails],
    );

    const handleDeleteTransactionSchedule = useCallback(
        async (record) => {
            try {
                await deleteTransactionSchedule(record.id);
                message.success("Мөр амжилттай устгагдлаа");
                if (selectedRecord?.id === record.id) {
                    setDrawerOpen(false);
                    setSelectedRecord(null);
                    setDetailData(null);
                    setStatusLogs([]);
                }
                await loadTransactions();
            } catch (error) {
                message.error(error.message || "Устгах боломжгүй");
            }
        },
        [loadTransactions, selectedRecord],
    );

    const manageActions = useTransactionScheduleManageActions({
        selectedRecord,
        refreshDetails,
        loadTransactions,
        refreshReturnsData,
    });

    const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const explicitTab = queryParams.get("tab");
    const queue = queryParams.get("queue");
    const openId = queryParams.get("openId");
    const status = queryParams.get("status");

    useEffect(() => {
        if (status === "EMPTY") {
            setSelectedParentDisplayStatus("EMPTY");
        }
        if (!status) {
            setSelectedParentDisplayStatus(undefined);
        }
    }, [status]);

    const filteredTransactions = useMemo(() => {
        // Exclude schedules that belong in ReturnedSchedulesTab
        let filtered = transactions.filter((x) => {
            const statuses = x.checkSummaryStatuses || [];
            return !statuses.includes("RETURN_RESET")
                && !statuses.includes("RETURNED_PARTIAL")
                && !statuses.includes("RETURNED_FULL");
        });

        if (searchRequestNumber.trim()) {
            const q = searchRequestNumber.trim().toLowerCase();
            filtered = filtered.filter((x) =>
                String(x.requestNumber || "").toLowerCase().includes(q),
            );
        }

        if (searchPayer.trim()) {
            const q = searchPayer.trim().toLowerCase();
            filtered = filtered.filter((x) =>
                String(x.payer || "").toLowerCase().includes(q),
            );
        }

        if (selectedParentDisplayStatus) {
            filtered = filtered.filter(
                (x) => x.parentDisplayStatus === selectedParentDisplayStatus,
            );
        }

        if (selectedCreatedAtRange?.length === 2) {
            const [start, end] = selectedCreatedAtRange;
            filtered = filtered.filter((x) => {
                if (!x.createdAt) return false;
                const date = dayjs(x.createdAt);
                return (
                    (date.isAfter(start.startOf("day")) || date.isSame(start.startOf("day"))) &&
                    (date.isBefore(end.endOf("day")) || date.isSame(end.endOf("day")))
                );
            });
        }

        if (queue === "waiting") {
            filtered = filtered.filter((x) => {
                const total = Number(x.allocationCount || 0);
                const agreed = Number(x.agreedAllocationCount || 0);
                return total > 0 && agreed < total;
            });
        }

        return filtered.sort((a, b) => {
            const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return bTime - aTime;
        });
    }, [
        queue,
        searchPayer,
        searchRequestNumber,
        selectedCreatedAtRange,
        selectedParentDisplayStatus,
        transactions,
    ]);

    const summary = useMemo(
        () => buildTransactionScheduleSummary(filteredTransactions),
        [filteredTransactions],
    );

    useEffect(() => {
        if (!openId || transactions.length === 0) return;
        const found = transactions.find((x) => String(x.id) === String(openId));
        if (found) {
            openDetails(found);
        }
    }, [openDetails, openId, transactions]);

    const initialTab = explicitTab === "returns"
        ? "returns"
        : returns.length > 0
            ? "returns"
            : "schedules";

    return {
        batch,
        canAgree,
        canDelete,
        canUnagree,
        isAuthor,

        transactions,
        filteredTransactions,
        summary,

        loadingTable,
        loadingDetails,
        loadingUnmatchedReturns,
        loadingReturnHistory,
        loadingIgnoredReturns,

        returns,
        returnHistory,
        ignoredReturns,

        drawerOpen,
        selectedRecord,
        detailData,
        statusLogs,

        manageOpen,
        manageRecord,

        searchRequestNumber,
        setSearchRequestNumber,
        searchPayer,
        setSearchPayer,
        selectedParentDisplayStatus,
        setSelectedParentDisplayStatus,
        selectedCreatedAtRange,
        setSelectedCreatedAtRange,

        setDrawerOpen,
        setSelectedRecord,
        setManageOpen,

        loadTransactions,
        loadUnmatchedReturns,
        loadReturnHistory,
        loadIgnoredReturns,
        refreshReturnsData,
        openManage,
        openDetails,
        refreshDetails,
        handleDeleteTransactionSchedule,

        initialTab,

        ...manageActions,
    };
}